import type { DateWindow } from '../../../../lib/filters.hooks.js';
import { chainRowsOf } from '../../ideation/chainRows.js';
import { parseResult } from '../../parsedResult.js';
import { isIterationsResult, recommendationsOf } from '../../recommendationRows.js';
import type { RunFilters } from '../../runFilters.js';
import { buildRunParams, openingValues } from '../../runParams.js';
import type {
  NextAction,
  Session,
  WorkflowDetail,
  WorkflowField,
  WorkflowRun,
} from '../../workflows.types.js';
import type { AlisonSession, AlisonStep } from './alisonRun.js';

export interface AlisonSetup {
  scope: { dateRange: DateWindow; accountIds?: number[]; filters?: RunFilters };
  leadingKpi: string;
  params?: Record<string, unknown>;
  paramsByAction?: Record<string, Record<string, unknown>>;
  parent?: { runId: string; selectItems: Record<string, unknown>[] };
}

export interface AlisonPick {
  selectItems: Record<string, unknown>[];
  params: Record<string, unknown>;
  count: number;
}

// Per step, by index: the run it became, the params it was sent, and how many rows it took.
export interface LiveProgress {
  path: string[];
  stopAt: number;
  setup: AlisonSetup;
  runIds: string[];
  params: Record<string, unknown>[];
  picked: number[];
  failed: string | null;
}

export function configFields(detail: WorkflowDetail | undefined): WorkflowField[] {
  return detail?.steps.find((step) => step.kind === 'config')?.fields ?? [];
}

export function stepParams(
  fields: WorkflowField[],
  inherited: Record<string, unknown>,
  leadingKpi: string,
): Record<string, unknown> {
  return buildRunParams(fields, openingValues(fields, null, inherited, leadingKpi));
}

// What `after` changes from `before`. A param put back to its default is left out of
// `after`, so it comes back as undefined and is not sent.
export function changedParams(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): Record<string, unknown> {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return Object.fromEntries(
    [...keys]
      .filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]))
      .map((key) => [key, after[key]]),
  );
}

// Every row the source offers. An iterations result is picked by recommendation, so
// each creative goes with all of its recommendations.
export function pickAll(
  source: WorkflowRun,
  edge: NextAction,
  fields: WorkflowField[],
  inherited: Record<string, unknown>,
  leadingKpi: string,
): AlisonPick {
  const params = stepParams(fields, inherited, leadingKpi);
  const resultData = parseResult(source.result, source.resultKind);
  const rows = chainRowsOf(source.result, source.resultKind, resultData).slice(
    0,
    edge.maxSelection ?? undefined,
  );
  if (isIterationsResult(source.result) && resultData.kind === 'other') {
    const picked = resultData.rows
      .flatMap((row) => {
        const ids = recommendationsOf(row.values).flatMap((entry) =>
          entry.id === null ? [] : [entry.id],
        );
        return row.creativeId === null || ids.length === 0 ? [] : [{ row, ids }];
      })
      .slice(0, edge.maxSelection ?? undefined);
    return {
      selectItems: picked.map(({ row }) => row.values),
      params: {
        ...params,
        recommendation_ids: Object.fromEntries(picked.map(({ row, ids }) => [row.creativeId, ids])),
      },
      count: picked.length,
    };
  }
  const selectItems = edge.requiresSelection ? rows.map((row) => row.values) : [];
  return { selectItems, params, count: selectItems.length };
}

export function liveSession(
  progress: LiveProgress,
  session: Session | null,
  calls: (index: number) => string | null,
): AlisonSession {
  const steps: AlisonStep[] = progress.runIds.flatMap((runId, index) => {
    const step = session?.steps.find((entry) => entry.runId === runId);
    return step === undefined ? [] : [{ ...step, alisonCall: calls(index) }];
  });
  const planned = progress.stopAt + 1;
  const done = steps.length === planned && steps.every((step) => step.status === 'completed');
  const failed = progress.failed !== null || steps.some((step) => step.status === 'failed');
  return {
    rootRunId: progress.runIds[0] ?? '',
    status: failed
      ? 'failed'
      : done
        ? planned === progress.path.length
          ? 'completed'
          : 'awaiting_decision'
        : 'running',
    scope: session?.scope ?? {
      startDate: progress.setup.scope.dateRange[0],
      endDate: progress.setup.scope.dateRange[1],
      accountIds: progress.setup.scope.accountIds ?? [],
      filters: {},
    },
    steps,
    automation: session?.automation ?? null,
    failure: progress.failed ?? steps.find((step) => step.status === 'failed')?.error ?? null,
  };
}
